/**
 * Size Breakdown Calculator
 * Splits a total shirt quantity across sizes using a curve chosen for the
 * age group being ordered for. Extended and youth sizes are optional.
 */

(function () {
  const LABELS = {
    yxs: 'Youth XS',
    ys: 'Youth S',
    ym: 'Youth M',
    yl: 'Youth L',
    yxl: 'Youth XL',
    xs: 'XS',
    s: 'S',
    m: 'M',
    l: 'L',
    xl: 'XL',
    '2xl': '2XL',
    '3xl': '3XL',
  };

  const YOUTH_ORDER = ['yxs', 'ys', 'ym', 'yl', 'yxl'];
  const ADULT_ORDER = ['xs', 's', 'm', 'l', 'xl', '2xl', '3xl'];

  // XS and 3XL are behind chips, so a group that doesn't call for them still
  // has to produce something sensible when someone switches them on.
  const OPTIONAL_KEYS = ['xs', '3xl'];
  const OPTIONAL_FALLBACK_SHARE = 0.03;

  // Used when youth sizes get switched on for a group that doesn't normally
  // need them (a high school order with a few younger siblings, say).
  const FALLBACK_YOUTH = { ys: 1, ym: 2, yl: 2, yxl: 1 };

  /**
   * Weights are percent-of-total, so `youth` and `adult` together sum to 100
   * and `youthShare` is just the youth half of that sum. Sourced from school
   * and group-order size guides — see the section comment for the reasoning.
   */
  const AGE_GROUPS = [
    {
      key: 'elementary',
      label: 'Elementary (PK–5)',
      note: 'Almost entirely youth sizes. Pre-K and kindergarten sit at Youth XS/S; by 4th–5th the bulk has moved to Youth L/XL.',
      youthShare: 94,
      youth: { yxs: 18, ys: 26, ym: 18, yl: 21, yxl: 11 },
      adult: { s: 5, m: 1 },
      toggles: { xs: false, '3xl': false, youth: true },
    },
    {
      key: 'middle',
      label: 'Middle school (6–8)',
      note: 'The hardest group to size — Youth XL and Adult S are the bridge, and 8th graders can be fully adult-sized.',
      youthShare: 58,
      youth: { ym: 7, yl: 23, yxl: 28 },
      adult: { s: 25, m: 13, l: 4 },
      toggles: { xs: false, '3xl': false, youth: true },
    },
    {
      key: 'high',
      label: 'High school (9–12)',
      note: 'Adult sizes throughout, with more smalls than a general adult order and a real tail at 2XL.',
      adult: { s: 15, m: 20, l: 30, xl: 20, '2xl': 11, '3xl': 4 },
      toggles: { xs: false, '3xl': true, youth: false },
    },
    {
      key: 'college',
      label: 'College',
      note: 'Skews smaller than a general adult order, especially for women-heavy groups — though the oversized-fit trend pulls some of it back up.',
      adult: { xs: 2, s: 20, m: 30, l: 27, xl: 13, '2xl': 6, '3xl': 2 },
      toggles: { xs: true, '3xl': true, youth: false },
    },
    {
      key: 'adults',
      label: 'Adults',
      // Goodink's own published recommendation, from a 15,000-order sample and
      // then nudged a size up on purpose — a shirt that runs big still gets
      // worn, a shirt that runs small ends up in the donation pile.
      note: 'Our house curve, built from 15,000 orders and nudged a little toward XL. L is the single most common size, and M–XL covers three quarters of the order.',
      adult: { xs: 1, s: 10, m: 20, l: 30, xl: 25, '2xl': 12, '3xl': 2 },
      toggles: { xs: false, '3xl': true, youth: false },
    },
  ];

  /**
   * Distribute `total` across `weights` so the parts always sum back to `total`.
   * Uses the largest remainder method: floor everything, then hand the leftover
   * units to the sizes that lost the most in rounding.
   */
  function allocate(total, weights) {
    const weightSum = weights.reduce((a, b) => a + b, 0);
    if (total <= 0 || weightSum <= 0) return weights.map(() => 0);

    const exact = weights.map((w) => (total * w) / weightSum);
    const parts = exact.map(Math.floor);
    const leftover = total - parts.reduce((a, b) => a + b, 0);

    const byRemainder = exact
      .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
      .sort((a, b) => b.remainder - a.remainder || weights[b.index] - weights[a.index]);

    for (let i = 0; i < leftover; i++) {
      parts[byRemainder[i % byRemainder.length].index]++;
    }

    return parts;
  }

  class SizeBreakdown {
    constructor(root) {
      this.root = root;
      this.totalInput = root.querySelector('[data-total]');
      this.groupInputs = Array.from(root.querySelectorAll('[data-group]'));
      this.groupNote = root.querySelector('[data-group-note]');
      this.youthToggle = root.querySelector('[data-toggle="youth"]');
      this.youthShareField = root.querySelector('[data-youth-share-field]');
      this.youthShareInput = root.querySelector('[data-youth-share]');
      this.error = root.querySelector('[data-error]');
      this.result = root.querySelector('[data-result]');
      this.tableBody = root.querySelector('[data-rows]');
      this.totalCell = root.querySelector('[data-total-cell]');
      this.copyButton = root.querySelector('[data-copy]');

      this.fallbackYouthShare = parseInt(root.dataset.defaultYouthShare, 10) || 25;

      // Fires before the bubbled listener below, so the preset is applied by
      // the time render() runs.
      this.groupInputs.forEach((input) => {
        input.addEventListener('change', () => this.applyGroupDefaults());
      });

      root.addEventListener('input', () => this.render());
      root.addEventListener('change', () => this.render());
      this.copyButton.addEventListener('click', () => this.copy());

      this.applyGroupDefaults();
      this.render();
    }

    group() {
      const checked = this.groupInputs.find((input) => input.checked);
      const key = checked && checked.value;
      return AGE_GROUPS.find((entry) => entry.key === key) || AGE_GROUPS[AGE_GROUPS.length - 1];
    }

    /** Reset the chips and youth percentage to what the chosen group calls for. */
    applyGroupDefaults() {
      const group = this.group();

      Object.entries(group.toggles).forEach(([key, on]) => {
        const toggle = this.root.querySelector(`[data-toggle="${key}"]`);
        if (toggle) toggle.checked = on;
      });

      this.youthShareInput.value = group.youthShare || this.fallbackYouthShare;
      if (this.groupNote) this.groupNote.textContent = group.note;
    }

    enabledToggle(key) {
      const toggle = this.root.querySelector(`[data-toggle="${key}"]`);
      return Boolean(toggle && toggle.checked);
    }

    /** Weighted, ordered list of adult sizes for the current group and chips. */
    adultSizes(group) {
      const weights = group.adult || {};
      const base = Object.values(weights).reduce((a, b) => a + b, 0);

      return ADULT_ORDER.map((key) => {
        const optional = OPTIONAL_KEYS.includes(key);
        if (optional && !this.enabledToggle(key)) return null;

        // An optional size the group doesn't list still gets a small slice.
        const weight = weights[key] || (optional ? base * OPTIONAL_FALLBACK_SHARE : 0);
        return weight > 0 ? { key, label: LABELS[key], weight } : null;
      }).filter(Boolean);
    }

    /** Weighted, ordered list of youth sizes for the current group. */
    youthSizes(group) {
      const weights = group.youth && Object.keys(group.youth).length ? group.youth : FALLBACK_YOUTH;

      return YOUTH_ORDER.map((key) =>
        weights[key] > 0 ? { key, label: LABELS[key], weight: weights[key] } : null
      ).filter(Boolean);
    }

    /** @returns {{label: string, qty: number}[]} */
    breakdown(total) {
      const group = this.group();
      const adult = this.adultSizes(group);

      if (!this.youthToggle.checked) {
        const parts = allocate(total, adult.map((size) => size.weight));
        return adult.map((size, i) => ({ label: size.label, qty: parts[i] }));
      }

      const youth = this.youthSizes(group);
      const share = Math.min(Math.max(parseInt(this.youthShareInput.value, 10) || 0, 0), 100);
      const youthTotal = Math.round((total * share) / 100);
      const adultTotal = total - youthTotal;

      const youthParts = allocate(youthTotal, youth.map((size) => size.weight));
      const adultParts = allocate(adultTotal, adult.map((size) => size.weight));

      return [
        ...youth.map((size, i) => ({ label: size.label, qty: youthParts[i] })),
        ...adult.map((size, i) => ({ label: size.label, qty: adultParts[i] })),
      ];
    }

    render() {
      this.youthShareField.hidden = !this.youthToggle.checked;

      const raw = this.totalInput.value.trim();
      const total = parseInt(raw.replace(/[^0-9]/g, ''), 10);
      const valid = raw !== '' && Number.isFinite(total) && total > 0;

      this.error.hidden = raw === '' || valid;
      this.result.hidden = !valid;
      if (!valid) return;

      const rows = this.breakdown(total);
      const max = Math.max(...rows.map((row) => row.qty), 1);

      this.tableBody.innerHTML = rows
        .map(
          (row) => `
            <div class="sbd-row">
              <span class="sbd-row-label">${row.label}</span>
              <div class="sbd-row-bar">
                <div class="sbd-row-bar-fill" style="width: ${(row.qty / max) * 100}%"></div>
              </div>
              <span class="sbd-row-qty">${row.qty.toLocaleString()}</span>
            </div>`
        )
        .join('');

      this.totalCell.textContent = total.toLocaleString();
      this.rows = rows;
    }

    copy() {
      if (!this.rows) return;

      const text = [`${this.group().label} size breakdown`]
        .concat(this.rows.map((row) => `${row.label}: ${row.qty}`))
        .concat(`Total: ${this.rows.reduce((sum, row) => sum + row.qty, 0)}`)
        .join('\n');

      navigator.clipboard.writeText(text).then(() => {
        const original = this.copyButton.textContent;
        this.copyButton.textContent = 'Copied!';
        setTimeout(() => {
          this.copyButton.textContent = original;
        }, 1600);
      });
    }
  }

  document.querySelectorAll('[data-size-breakdown]').forEach((root) => new SizeBreakdown(root));
})();
